import React, { useState } from "react";
import { AlertTriangle, Loader2, CheckCircle2, XCircle } from "lucide-react";

import { BATCH_SIZE, runChunkedBulkDelete } from "@/components/bulk/runChunkedBulkDelete";

/**
 * Confirmation + progress modal for bulk deletion.
 *
 * Behaviour:
 *   • On open the caller passes `ids` (already deduped) and a `deleter`
 *     which knows the API endpoint (`api.bulkDelete{Users,Projects,...}`).
 *   • The modal shows the confirmation UI first — primary button text
 *     is `Delete N {noun}` per the spec.
 *   • On confirm, the modal switches to a live progress view running
 *     one batch at a time via `runChunkedBulkDelete`. The primary
 *     button becomes "Close" only once the operation is done or has
 *     been stopped by a failing batch.
 *   • When done, `onDone(agg)` fires so the parent can refresh its
 *     list and clear selection. `onCancel` closes the modal (only
 *     enabled when idle or after a run completes).
 */
export default function BulkDeleteModal({
    open,
    onCancel,
    onDone,
    ids,
    labelSingular,
    labelPlural,
    deleter,
    previewFetcher = null,   // async (ids) => { totals: {collection: count}, found_count, not_found_count }
    extraWarning = null,
    testidPrefix = "bulk-delete",
}) {
    const [phase, setPhase] = useState("confirm"); // confirm | running | done | stopped
    const [progress, setProgress] = useState(null);
    const [result, setResult] = useState(null);
    const [errorLine, setErrorLine] = useState("");
    const [preview, setPreview] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);

    React.useEffect(() => {
        if (!open) {
            setPhase("confirm");
            setProgress(null);
            setResult(null);
            setErrorLine("");
            setPreview(null);
            return;
        }
        // Fetch preview counts if the caller provided a fetcher.
        if (previewFetcher && ids?.length) {
            setPreviewLoading(true);
            previewFetcher(ids)
                .then((p) => setPreview(p))
                .catch(() => setPreview(null))
                .finally(() => setPreviewLoading(false));
        }
    }, [open, previewFetcher, ids]);

    if (!open) return null;

    const count = ids?.length || 0;
    const noun = count === 1 ? labelSingular : labelPlural;
    const totalBatches = Math.max(1, Math.ceil(count / BATCH_SIZE));

    async function runIt() {
        setPhase("running");
        setErrorLine("");
        try {
            const agg = await runChunkedBulkDelete(ids, deleter, (p) => setProgress(p));
            setResult(agg);
            setPhase(agg.stopped ? "stopped" : "done");
        } catch (e) {
            // runChunkedBulkDelete swallows per-batch errors — this catch
            // only fires on a *pre-flight* problem (e.g. empty list).
            setErrorLine(e?.message || "Delete failed");
            setPhase("stopped");
        }
    }

    function close() {
        if (phase === "done" || phase === "stopped") {
            onDone?.(result);
        }
        onCancel?.();
    }

    const closable = phase === "confirm" || phase === "done" || phase === "stopped";

    return (
        <div
            className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 px-3"
            role="dialog"
            aria-modal="true"
            data-testid={`${testidPrefix}-modal`}
            onKeyDown={(e) => {
                if (e.key === "Escape" && closable) close();
            }}
        >
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
                {phase === "confirm" && (
                    <>
                        <div className="p-5 md:p-6">
                            <div className="flex items-start gap-3">
                                <div className="w-10 h-10 rounded-full bg-[#FEE2E2] flex items-center justify-center shrink-0">
                                    <AlertTriangle size={18} className="text-[#B91C1C]" strokeWidth={2} />
                                </div>
                                <div className="min-w-0">
                                    <div
                                        className="text-base font-medium text-[#131B2F]"
                                        data-testid={`${testidPrefix}-title`}
                                    >
                                        Delete {count} {noun}?
                                    </div>
                                    <p className="mt-1 text-sm text-[#4B5565]">
                                        You're about to permanently delete {count} {noun}. This action{" "}
                                        <span className="font-medium text-[#B91C1C]">cannot be undone</span>.
                                    </p>
                                    {previewLoading && (
                                        <p className="mt-2 text-xs text-[#4B5565]">Computing what will be removed…</p>
                                    )}
                                    {preview && preview.totals && Object.keys(preview.totals).length > 0 && (
                                        <div className="mt-3 border border-[#E5E7EB] rounded-md overflow-hidden" data-testid={`${testidPrefix}-impact`}>
                                            <div className="px-3 py-1.5 bg-[#F5F5F7] text-[11px] uppercase tracking-[0.08em] text-[#4B5565] font-medium">
                                                Records that will also be removed
                                            </div>
                                            <table className="w-full text-xs">
                                                <tbody>
                                                    {Object.entries(preview.totals)
                                                        .filter(([, v]) => v > 0)
                                                        .sort((a, b) => b[1] - a[1])
                                                        .map(([k, v]) => (
                                                            <tr key={k} className="border-t border-[#F3F4F6] first:border-t-0">
                                                                <td className="px-3 py-1 text-[#4B5565]">{k}</td>
                                                                <td className="px-3 py-1 text-right font-medium text-[#131B2F]">{v.toLocaleString()}</td>
                                                            </tr>
                                                        ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                    {totalBatches > 1 && (
                                        <p
                                            className="mt-2 text-xs text-[#4B5565] bg-[#F5F5F7] rounded px-2 py-1.5"
                                            data-testid={`${testidPrefix}-batching-hint`}
                                        >
                                            Because you selected more than {BATCH_SIZE}, we'll process this in{" "}
                                            <span className="font-medium">{totalBatches} batches</span>. You can watch progress live.
                                        </p>
                                    )}
                                    {extraWarning && (
                                        <p className="mt-2 text-xs text-[#B45309] bg-[#FEF3C7] rounded px-2 py-1.5">
                                            {extraWarning}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="px-5 md:px-6 py-3 bg-[#F9FAFB] rounded-b-xl flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={close}
                                className="h-10 px-4 rounded-md border border-[#E5E7EB] bg-white text-sm text-[#131B2F] hover:bg-[#F5F5F7]"
                                data-testid={`${testidPrefix}-cancel`}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={runIt}
                                disabled={count === 0}
                                className="h-10 px-4 rounded-md bg-[#DA3535] hover:bg-[#B92E2E] text-white text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-60"
                                data-testid={`${testidPrefix}-confirm`}
                            >
                                Delete {count} {noun}
                            </button>
                        </div>
                    </>
                )}

                {phase === "running" && progress && (
                    <div className="p-5 md:p-6" data-testid={`${testidPrefix}-progress`}>
                        <div className="flex items-center gap-3">
                            <Loader2 size={18} className="text-[#131B2F] animate-spin" />
                            <div>
                                <div className="text-base font-medium text-[#131B2F]">
                                    Deleting batch {progress.batchNumber} of {progress.totalBatches}
                                </div>
                                <div
                                    className="text-sm text-[#4B5565] mt-0.5"
                                    data-testid={`${testidPrefix}-progress-line`}
                                >
                                    {progress.deletedSoFar} of {progress.total} {noun} deleted
                                </div>
                            </div>
                        </div>
                        <div className="mt-4 h-2 bg-[#E5E7EB] rounded-full overflow-hidden">
                            <div
                                className="h-full bg-[#3E9E2E] transition-all"
                                style={{ width: `${Math.min(100, (progress.deletedSoFar / Math.max(1, progress.total)) * 100)}%` }}
                            />
                        </div>
                        <p className="mt-3 text-xs text-[#4B5565]">
                            Please leave this open. If you close now, in-flight batches finish server-side but you won't see the summary.
                        </p>
                    </div>
                )}

                {(phase === "done" || phase === "stopped") && result && (
                    <>
                        <div className="p-5 md:p-6" data-testid={`${testidPrefix}-summary`}>
                            <div className="flex items-start gap-3">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${result.stopped ? "bg-[#FEE2E2]" : "bg-[#E6F1DE]"}`}>
                                    {result.stopped
                                        ? <XCircle size={18} className="text-[#B91C1C]" />
                                        : <CheckCircle2 size={18} className="text-[#2E7E22]" />}
                                </div>
                                <div className="min-w-0">
                                    <div className="text-base font-medium text-[#131B2F]">
                                        {result.stopped
                                            ? `Stopped at batch ${result.failedBatch?.number || "?"} of ${result.batches.length + Math.ceil(result.skipped / BATCH_SIZE)}`
                                            : `Deleted ${result.deleted} of ${result.total} ${noun}`}
                                    </div>
                                    <dl className="mt-2 text-sm grid grid-cols-2 gap-x-4 gap-y-1">
                                        <SummaryRow label="Total selected" value={result.total} testid={`${testidPrefix}-summary-total`} />
                                        <SummaryRow label="Deleted" value={result.deleted} tone="ok" testid={`${testidPrefix}-summary-deleted`} />
                                        <SummaryRow label="Failed" value={result.failed} tone={result.failed ? "warn" : "muted"} testid={`${testidPrefix}-summary-failed`} />
                                        {result.skipped > 0 && (
                                            <SummaryRow label="Skipped" value={result.skipped} tone="warn" testid={`${testidPrefix}-summary-skipped`} />
                                        )}
                                    </dl>
                                    {result.stopped && (
                                        <div
                                            className="mt-3 text-xs text-[#B91C1C] bg-[#FEE2E2] rounded px-2 py-2"
                                            data-testid={`${testidPrefix}-batch-error`}
                                        >
                                            <div className="font-medium">
                                                Batch {result.failedBatch?.number} failed
                                                {result.failedBatch?.size ? ` (${result.failedBatch.size} ${noun})` : ""}.
                                            </div>
                                            <div className="mt-0.5">Reason: {result.failedBatch?.error || "unknown"}</div>
                                            <div className="mt-1">
                                                {result.deleted} {result.deleted === 1 ? labelSingular : labelPlural} already deleted;{" "}
                                                {result.skipped} still remaining. Nothing was retried automatically.
                                            </div>
                                        </div>
                                    )}
                                    {result.failed > 0 && result.failures?.length > 0 && (
                                        <details className="mt-3 text-xs text-[#4B5565]">
                                            <summary className="cursor-pointer">Per-record failures ({result.failed})</summary>
                                            <ul className="mt-1 space-y-0.5 max-h-32 overflow-y-auto">
                                                {result.failures.slice(0, 50).map((f, idx) => (
                                                    <li key={`${f.id}-${idx}`} className="truncate">
                                                        <span className="font-mono">{f.id}</span> — {f.reason}
                                                    </li>
                                                ))}
                                                {result.failures.length > 50 && (
                                                    <li>… and {result.failures.length - 50} more</li>
                                                )}
                                            </ul>
                                        </details>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="px-5 md:px-6 py-3 bg-[#F9FAFB] rounded-b-xl flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={close}
                                className="h-10 px-4 rounded-md bg-[#131B2F] hover:bg-[#0B1220] text-white text-sm font-medium"
                                data-testid={`${testidPrefix}-close`}
                            >
                                Close
                            </button>
                        </div>
                    </>
                )}

                {phase === "stopped" && !result && errorLine && (
                    <div className="p-5 md:p-6" data-testid={`${testidPrefix}-preflight-error`}>
                        <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-full bg-[#FEE2E2] flex items-center justify-center shrink-0">
                                <XCircle size={18} className="text-[#B91C1C]" />
                            </div>
                            <div>
                                <div className="text-base font-medium text-[#131B2F]">Couldn't start delete</div>
                                <p className="mt-1 text-sm text-[#4B5565]">{errorLine}</p>
                            </div>
                        </div>
                        <div className="mt-4 flex justify-end">
                            <button
                                type="button"
                                onClick={close}
                                className="h-10 px-4 rounded-md bg-[#131B2F] text-white text-sm font-medium"
                                data-testid={`${testidPrefix}-close`}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}


function SummaryRow({ label, value, tone = "muted", testid }) {
    const tones = {
        ok:    "text-[#2E7E22]",
        warn:  "text-[#B45309]",
        muted: "text-[#131B2F]",
    };
    return (
        <>
            <dt className="text-[#4B5565]">{label}</dt>
            <dd className={`font-medium text-right ${tones[tone] || tones.muted}`} data-testid={testid}>
                {value}
            </dd>
        </>
    );
}
